import { useCallback, useState } from 'react';
import { ApiError } from '@/api/client';
import { readLimosaPdfAsAdministratie, uploadLimosaAsAdministratie, type LimosaPerson, type LimosaProject } from '@/api/administratie';
import { compressImage } from '@/utils/imageCompression';
import { LimosaFileFields, LimosaFilePicker, useLimosaFileForm } from '@/components/limosa/LimosaFileStep';
import { LimosaProjects } from './LimosaProjects';

/**
 * Administratie uploads a Limosa it received by mail or WhatsApp, for one person of
 * this installer row. The API stores it approved at once. The file comes first; a PDF
 * fills in the fields. It is linked to the werf row's project; administratie may change that.
 */
export function AdminLimosaUpload({
  installerId,
  people,
  initialProjects,
  onUploaded,
  onCancel,
}: {
  installerId: number;
  people: LimosaPerson[];
  /** The werf row's project, pre-filled. */
  initialProjects: LimosaProject[];
  onUploaded: () => void;
  onCancel: () => void;
}) {
  const [partnerId, setPartnerId] = useState<number>(people[0]?.id ?? 0);
  const [projects, setProjects] = useState<LimosaProject[]>(initialProjects);
  const readPdf = useCallback((f: File) => readLimosaPdfAsAdministratie(installerId, f), [installerId]);
  const form = useLimosaFileForm(readPdf);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const { file, geldigVan, geldigTot } = form;
    if (!file) return setError('Kies een PDF of foto van de Limosa.');
    if (!geldigVan || !geldigTot) return setError('Vul geldig van en geldig tot in.');
    if (geldigVan > geldigTot) return setError('Geldig tot ligt voor geldig van.');

    setBusy(true);
    try {
      const prepared = await compressImage(file);
      await uploadLimosaAsAdministratie(installerId, { partnerId, geldigVan, geldigTot, limosaNummer: form.limosaNummer.trim() || undefined, projectIds: projects.map((p) => p.id), file: prepared });
      onUploaded();
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : 'Limosa opladen mislukt');
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} aria-label="Limosa uploaden" className="mt-2 border border-gray-200 rounded-lg p-3 space-y-2 text-sm">
      <LimosaFilePicker form={form} />
      {people.length > 1 ? (
        <label className="block text-xs text-td-muted">
          Voor wie
          <select value={partnerId} onChange={(e) => setPartnerId(Number(e.target.value))} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm">
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      ) : (
        <p className="text-xs text-td-muted">Voor {people[0]?.name}</p>
      )}
      <LimosaFileFields form={form} person={people.find((p) => p.id === partnerId)} compact />
      <LimosaProjects value={projects} onChange={setProjects} disabled={busy} />
      <p className="text-xs text-td-light">Een Limosa die je zelf oplaadt, is meteen goedgekeurd.</p>
      {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy || !form.ready} className="px-3 py-1.5 text-xs font-semibold text-white bg-td-blue rounded-lg disabled:opacity-60">
          {busy ? 'Bezig met opladen…' : 'Opladen'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className="px-3 py-1.5 text-xs font-semibold text-td-muted border border-gray-300 rounded-lg disabled:opacity-60">
          Annuleren
        </button>
      </div>
    </form>
  );
}
